import { API, axiosInstance, handleResponse, graphqlAxiosInstance } from './helper';
import { ROLES } from '../constants';
import { getRealmUserInfo } from './keycloak';

export interface PatientManagementDataPayload {
  startTimestamp: string;
  endTimestamp: string;
}

export interface PatientManagementTrendsPayload {
  rangeType: string;
  organisationId?: string;
}

export interface PatientDetailsPayload {
  patientId: string;
}

export interface PatientManagementTablePayload {
  limit: number;
  created_at?: string;
}

// Helper function to get organisationId from token for practice admins
const getOrganisationIdIfPracticeAdmin = () => {
  const userRole = localStorage.getItem('userRole') || '';

  if (userRole === ROLES.PRACTISE_ADMIN) {
    const token = localStorage.getItem('user_access_token') || '';
    if (token) {
      try {
        // Parse the JWT token to get the payload
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        // Return organisation ID from token if it exists
        if (tokenPayload['x-hasura-org-id']) {
          return tokenPayload['x-hasura-org-id'];
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }

  return null;
};

const getPatientsStats = async (payload: PatientManagementDataPayload) =>
  await handleResponse(axiosInstance.post(API.getPatientsStats, payload));

const getAppointmentTrends = async (payload: PatientManagementDataPayload) =>
  await handleResponse(axiosInstance.post(API.getAppointmentTrends, payload));

const getRegistrationTrends = async (payload: PatientManagementTrendsPayload) => {
  // Check if user is Practice Admin
  const organisationId = getOrganisationIdIfPracticeAdmin();

  // Add organisation ID if practice admin
  if (organisationId) {
    payload.organisationId = organisationId;
  }

  return await handleResponse(axiosInstance.post(API.getRegistrationTrends, payload));
};

const getVerificationTrends = async (payload: PatientManagementTrendsPayload) => {
  // Check if user is Practice Admin
  const organisationId = getOrganisationIdIfPracticeAdmin();

  // Add organisation ID if practice admin
  if (organisationId) {
    payload.organisationId = organisationId;
  }

  return await handleResponse(axiosInstance.post(API.getVerificationTrends, payload));
};

const getPatientProfileDetailsById = async (payload: PatientDetailsPayload) =>
  await handleResponse(axiosInstance.post(API.getPatientProfileDetailsById, payload));

const getPatientManagementTableData = async (
  limit = 10,
  created_at?: string,
  AgeStart?: string,
  AgeEnd?: string,
  gender?: string
) => {
  // Base where clause

  // Construct query string dynamically
  const query = `
    query get_Patient_users_patient_management_v1(
      $limit: Int!, 
      $created_at: timestamptz, 
      ${gender ? '$gender: String,' : ''} 
      ${AgeStart && AgeEnd ? '$AgeStart: date, $AgeEnd: date,' : ''}
    ) @cached(ttl: 300) {
      patientUsers(
        order_by: { created_at: desc }, 
        where: {
          verificationStatus: { _eq: "verified" },
          created_at: { _lt: $created_at }
          ${gender ? 'gender: { _eq: $gender },' : ''}
          ${AgeStart && AgeEnd ? 'DOB: { _gte: $AgeStart, _lt: $AgeEnd },' : ''}
        }, 
        limit: $limit
      ) {
        DOB
        id
        created_at
        gender
        verificationStatus
        patientUserDocuments {
          imageContent
        }
        Appointments(distinct_on: practiseId, where: {status: {_eq: "approved"}}, limit: 6) {
          practiseUserProfileRelation {
            practiseUserLivePicRelation {
              imageContent
              practiseAdminId
            }
          }
        }
      }
      totalCount: patientUsers_aggregate(
        where: {
          verificationStatus: { _eq: "verified" }
          ${gender ? 'gender: { _eq: $gender },' : ''}
          ${AgeStart && AgeEnd ? 'DOB: { _gte: $AgeStart, _lt: $AgeEnd },' : ''}
        }
      ) {
        aggregate {
          count
        }
      }
    }
  `;

  type PatientManagementTableVariables = {
    limit: number;
    created_at: string;
    gender?: string;
    AgeStart?: string;
    AgeEnd?: string;
  };

  // Build variables object dynamically
  const variables: PatientManagementTableVariables = {
    limit,
    created_at: created_at || new Date().toISOString()
  };

  if (gender) variables.gender = gender;
  if (AgeStart && AgeEnd) {
    variables.AgeStart = AgeStart;
    variables.AgeEnd = AgeEnd;
  }

  const response = await graphqlAxiosInstance.post(`/v1/graphql`, {
    query,
    variables
  });

  return response;
};

const getPatientManagementTableEnrichedDataWithKeycloak = async (
  limit = 10,
  created_at?: string,
  AgeStart?: string,
  AgeEnd?: string,
  gender?: string
) => {
  const response = await getPatientManagementTableData(limit, created_at, AgeStart, AgeEnd, gender);
  const patientList = response?.data?.data?.patientUsers || [];
  const accessToken = localStorage.getItem('user_access_token') || '';

  const enrichedList = await Promise.all(
    patientList.map(async (doc: Patient, index: number) => {
      let email = doc.email || null;
      let firstName = doc.firstName || null;
      let lastName = doc.lastName || null;
      try {
        if (doc.id) {
          const keycloakData = await getRealmUserInfo(doc.id, accessToken);
          email = keycloakData.email;
          firstName = keycloakData.firstName;
          lastName = keycloakData.lastName;
        }
      } catch (error) {
        // fallback to nulls
      }
      const connectedUsers = await Promise.all(
        (doc.Appointments || []).map(async (appt: Appointment) => {
          const livePic = appt?.practiseUserProfileRelation?.practiseUserLivePicRelation;
          if (livePic?.imageContent && livePic?.practiseAdminId) {
            const doctorInfo = await getRealmUserInfo(livePic.practiseAdminId, accessToken);
            return {
              name: `${doctorInfo.firstName} ${doctorInfo.lastName}`,
              image: livePic.imageContent
            };
          }
          return null;
        })
      );
      return {
        id: doc.id,
        sNo: index + 1,
        fullName: firstName && lastName ? `${firstName} ${lastName}` : 'N/A',
        gender: doc.gender || '',
        verifiedOn: doc.created_at,
        email: email || 'N/A',
        image: doc.patientUserDocuments?.imageContent || '',
        role: doc.role || '',
        connectedUsers: connectedUsers.filter(Boolean)
      };
    })
  );
  let totalCount = 0;
  if (response?.data?.data?.totalCount?.aggregate?.count !== undefined) {
    totalCount = response.data.data.totalCount.aggregate.count;
  }
  return {
    data: enrichedList,
    hasMore: enrichedList.length === limit,
    totalCount,
    lastUpdatedAt: patientList.length > 0 ? patientList[patientList.length - 1].created_at : null
  };
};

export {
  getPatientsStats,
  getAppointmentTrends,
  getRegistrationTrends,
  getVerificationTrends,
  getPatientProfileDetailsById,
  getPatientManagementTableData,
  getPatientManagementTableEnrichedDataWithKeycloak
};

interface Appointment {
  practiseUserProfileRelation?: {
    practiseUserLivePicRelation?: {
      imageContent?: string;
      practiseAdminId?: string;
    };
  };
}

interface Patient {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  created_at?: string;
  verificationStatus?: string;
  patientUserDocuments?: {
    imageContent?: string;
  };
  role?: string;
  Appointments?: Appointment[];
}
